import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, ChevronRight, Plus, Upload, Download, X, AlertCircle, CheckCircle2, Loader2,
  User
} from 'lucide-react';
import {
  getContacts, createContact, uploadContactsCSV, downloadContactsTemplate
} from '../../services/adminDealFlowApi';
import { TENANT } from '../../config';
import { Card, CardContent } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { Input } from '../../components/new-components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/new-components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/new-components/ui/select';

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const CONTACT_TYPES = [
  { value: 'prospect', label: 'Prospect' },
  // Only a tenant that sells investments has investors to keep in contact with.
  ...(TENANT.features.investing ? [{ value: 'investor', label: 'Investor' }] : []),
  { value: 'partner', label: 'Partner' },
  { value: 'lead', label: 'Lead' },
  { value: 'vendor', label: 'Vendor' },
];

const ACCREDITATION_OPTIONS = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'pending', label: 'Pending' },
  { value: 'verified', label: 'Verified' },
  { value: 'expired', label: 'Expired' },
];

/* ------------------------------------------------------------------ */
/*  Add Contact Form                                                  */
/* ------------------------------------------------------------------ */
interface ContactFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company: string;
  title: string;
  contact_type: string;
  source: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  accreditation_status: string;
  notes: string;
}

const emptyForm: ContactFormData = {
  first_name: '', last_name: '', email: '', phone: '',
  company: '', title: '', contact_type: 'prospect', source: '',
  address: '', city: '', state: '', zip: '',
  accreditation_status: 'unknown', notes: ''
};

/* ------------------------------------------------------------------ */
/*  Main Component                                                    */
/* ------------------------------------------------------------------ */
const Contacts: React.FC = () => {
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);

  // List state
  const [contacts, setContacts] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 25;

  // Add contact dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState<ContactFormData>({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // CSV upload dialog
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [uploadError, setUploadError] = useState('');

  /* ---------------------- Data fetching ---------------------- */
  const load = async () => {
    try {
      setLoading(true);
      const res = await getContacts({ search, contact_type: typeFilter, page, limit });
      setContacts(res.data?.data || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [search, typeFilter, page]);

  /* ---------------------- Add contact ---------------------- */
  const handleAddContact = async () => {
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setFormError('First name and last name are required');
      return;
    }
    try {
      setSaving(true);
      setFormError('');
      await createContact(formData);
      setShowAddDialog(false);
      setFormData({ ...emptyForm });
      load();
    } catch (err: any) {
      setFormError(err?.response?.data?.error || err.message || 'Failed to create contact');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (key: keyof ContactFormData, value: string) =>
    setFormData(prev => ({ ...prev, [key]: value }));

  /* ---------------------- CSV Upload ---------------------- */
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCsvFile(file);
      setUploadError('');
      setUploadResult(null);
    }
  };

  const handleUploadCSV = async () => {
    if (!csvFile) return;
    try {
      setUploading(true);
      setUploadError('');
      const res = await uploadContactsCSV(csvFile);
      setUploadResult(res.data);
      setCsvFile(null);
      if (fileInput.current) fileInput.current.value = '';
      load(); // Refresh list
    } catch (err: any) {
      setUploadError(
        err?.response?.data?.error || err?.response?.data?.message || err.message || 'Upload failed'
      );
    } finally {
      setUploading(false);
    }
  };

  /* ---------------------- Template Download ---------------------- */
  const handleDownloadTemplate = () => {
    const headers = 'first_name,last_name,email,phone,company,title,contact_type,source,address,city,state,zip,accreditation_status,notes';
    const sample = 'Jane,Doe,jane@example.com,555-123-4567,Acme Corp,Administrator,prospect,referral,123 Main St,Austin,TX,78701,verified,Referred by a partner';
    const csv = headers + '\n' + sample + '\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ---------------------- Badge color helper ---------------------- */
  const typeBadgeVariant = (t: string): 'default' | 'secondary' | 'outline' | 'destructive' => {
    switch (t) {
      case 'investor': return 'default';
      case 'prospect': return 'secondary';
      case 'partner': return 'outline';
      default: return 'secondary';
    }
  };

  return (
    <div className="space-y-6">
      {/* ======== Header ======== */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-display font-semibold text-[#242424]">Contacts</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total} contacts — Manage relationships, prospects, and partners
          </p>
        </div>
        <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 w-full sm:w-auto">
          <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
            <Download className="h-4 w-4 mr-1.5" /> Template
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setShowUploadDialog(true); setUploadResult(null); setUploadError(''); setCsvFile(null); }}>
            <Upload className="h-4 w-4 mr-1.5" /> Upload CSV
          </Button>
          <Button size="sm" onClick={() => { setShowAddDialog(true); setFormData({ ...emptyForm }); setFormError(''); }}>
            <Plus className="h-4 w-4 mr-1.5" /> Add Contact
          </Button>
        </div>
      </div>

      {/* ======== Filters ======== */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or company..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-9"
          />
        </div>
        <Select value={typeFilter} onValueChange={(v: string) => { setTypeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {CONTACT_TYPES.map(t => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* ======== Table ======== */}
      <Card className="border-border">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-4 text-xs text-muted-foreground">Name</th>
                <th className="text-left p-4 text-xs text-muted-foreground">Email</th>
                <th className="text-left p-4 text-xs text-muted-foreground">Company</th>
                <th className="text-left p-4 text-xs text-muted-foreground">Type</th>
                <th className="text-left p-4 text-xs text-muted-foreground">Status</th>
                <th className="text-right p-4 text-xs text-muted-foreground">Invested</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-12 text-center text-muted-foreground">
                  <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto" />
                </td></tr>
              ) : (contacts || []).map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-border hover:bg-muted/50 cursor-pointer"
                  onClick={() => navigate(`/admin/contacts/${c.id}`)}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-[#F0F0F0] rounded-full flex items-center justify-center border border-border">
                        <span className="text-sm font-semibold text-[hsl(var(--chart-2))]">
                          {(c.first_name || '?')[0]}{(c.last_name || '?')[0]}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#242424]">
                          {c.first_name} {c.last_name}
                        </p>
                        {c.title && (
                          <p className="text-xs text-muted-foreground">{c.title}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">{c.email || '—'}</td>
                  <td className="p-4 text-sm text-muted-foreground">{c.company || '—'}</td>
                  <td className="p-4">
                    <Badge variant={typeBadgeVariant(c.contact_type)} className="text-xs capitalize">
                      {c.contact_type || 'prospect'}
                    </Badge>
                  </td>
                  <td className="p-4">
                    <Badge variant={c.accreditation_status === 'verified' ? 'default' : 'secondary'} className="text-xs capitalize">
                      {c.accreditation_status || 'unknown'}
                    </Badge>
                  </td>
                  <td className="p-4 text-sm text-right text-[#242424] font-medium">
                    {formatCurrency(c.total_invested || 0)}
                  </td>
                  <td className="p-4 text-right">
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </td>
                </tr>
              ))}
              {!loading && contacts.length === 0 && (
                <tr><td colSpan={7} className="p-12 text-center text-muted-foreground">
                  No contacts found. Click "Add Contact" or "Upload CSV" to get started.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* ======== Pagination ======== */}
      {total > limit && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              Previous
            </Button>
            <Button size="sm" variant="outline" disabled={page * limit >= total} onClick={() => setPage(p => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      {/* ======== Add Contact Dialog ======== */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" /> Add New Contact
            </DialogTitle>
          </DialogHeader>

          {formError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-md text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" /> {formError}
            </div>
          )}

          <div className="grid gap-4 py-2">
            {/* Name row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">First Name *</label>
                <Input value={formData.first_name} onChange={e => updateField('first_name', e.target.value)} placeholder="Jane" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Last Name *</label>
                <Input value={formData.last_name} onChange={e => updateField('last_name', e.target.value)} placeholder="Doe" />
              </div>
            </div>

            {/* Email & Phone */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Email</label>
                <Input type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} placeholder="jane@example.com" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Phone</label>
                <Input value={formData.phone} onChange={e => updateField('phone', e.target.value)} placeholder="555-123-4567" />
              </div>
            </div>

            {/* Company & Title */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Company</label>
                <Input value={formData.company} onChange={e => updateField('company', e.target.value)} placeholder="Acme Corp" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Title</label>
                <Input value={formData.title} onChange={e => updateField('title', e.target.value)} placeholder="CEO" />
              </div>
            </div>

            {/* Type & Accreditation */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Contact Type</label>
                <Select value={formData.contact_type} onValueChange={(v: string) => updateField('contact_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONTACT_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Accreditation</label>
                <Select value={formData.accreditation_status} onValueChange={(v: string) => updateField('accreditation_status', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACCREDITATION_OPTIONS.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Source */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Source</label>
              <Input value={formData.source} onChange={e => updateField('source', e.target.value)} placeholder="e.g. referral, website, event" />
            </div>

            {/* Address */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Address</label>
              <Input value={formData.address} onChange={e => updateField('address', e.target.value)} placeholder="123 Main St" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Input value={formData.city} onChange={e => updateField('city', e.target.value)} placeholder="City" />
              <Input value={formData.state} onChange={e => updateField('state', e.target.value)} placeholder="TX" maxLength={2} className="uppercase" />
              <Input value={formData.zip} onChange={e => updateField('zip', e.target.value)} placeholder="78701" />
            </div>

            {/* Notes */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Notes</label>
              <textarea
                value={formData.notes}
                onChange={e => updateField('notes', e.target.value)}
                placeholder="Additional notes..."
                className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddContact} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />}
              Add Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ======== Upload CSV Dialog ======== */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" /> Upload Contacts CSV
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Upload a CSV file to bulk-import contacts. Maximum 500 contacts per upload.
            </p>

            {/* Template download link */}
            <button
              onClick={handleDownloadTemplate}
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              <Download className="h-3.5 w-3.5" /> Download sample template
            </button>

            {/* File input */}
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <input
                ref={fileInput}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                id="csv-file-input"
              />
              {csvFile ? (
                <div className="flex items-center justify-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium">{csvFile.name}</span>
                  <button onClick={() => { setCsvFile(null); if (fileInput.current) fileInput.current.value = ''; }} className="text-muted-foreground hover:text-destructive">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label htmlFor="csv-file-input" className="cursor-pointer">
                  <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm font-medium">Click to select a CSV file</p>
                  <p className="text-xs text-muted-foreground mt-1">or drag and drop</p>
                </label>
              )}
            </div>

            {/* Upload error */}
            {uploadError && (
              <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>{uploadError}</div>
              </div>
            )}

            {/* Upload result */}
            {uploadResult && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-md text-sm">
                  <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{uploadResult.message}</p>
                    <p className="text-xs mt-0.5">
                      {uploadResult.imported} imported · {uploadResult.failed} failed
                    </p>
                  </div>
                </div>
                {uploadResult.errors?.length > 0 && (
                  <div className="max-h-40 overflow-y-auto border rounded-md p-2 text-xs space-y-1">
                    {uploadResult.errors.map((e: any, i: number) => (
                      <div key={i} className="text-red-600">
                        Row {e.row}: {e.errors?.join(', ')}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              {uploadResult ? 'Close' : 'Cancel'}
            </Button>
            {!uploadResult && (
              <Button onClick={handleUploadCSV} disabled={!csvFile || uploading}>
                {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
                Upload
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contacts;
